import { useState, useRef, useCallback, useEffect } from 'react';
import { RealtimeClient } from '@openai/realtime-api-beta';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { relayConfig } from '../utils/api';
import { getAuthHeadersData, getUserInfo } from '../utils/api/auth-bridge.js';
import { getSelectedTenantApiKey } from '../utils/roles.js';

const EMPTY_AUDIO = new Float32Array();

/**
 * Hook to manage realtime audio connection using OpenAI Realtime API
 * @param {Object} config - Configuration object
 * @param {Function} config.toggleMode - Callback to toggle between 'transcribe' and 'realtime' modes
 * @returns {Object} Realtime connection state and control functions
 * @returns {Array} returns.items - Conversation items from realtime session
 * @returns {boolean} returns.isConnected - Whether realtime connection is active
 * @returns {boolean} returns.isRecording - Whether currently recording audio
 * @returns {Float32Array} returns.audioData - Audio frequency data for visualization
 * @returns {Function} returns.connectConversation - Establish realtime connection
 * @returns {Function} returns.disconnectConversation - Disconnect realtime connection
 * @returns {Function} returns.startRecording - Start recording audio
 * @returns {Function} returns.stopRecording - Stop recording audio
 * @returns {Function} returns.deleteConversationItem - Delete a conversation item
 * @returns {string|null} returns.realtimeError - Error state if any
 * @returns {string|null} returns.realtimeChatId - Chat ID from realtime session
 * @returns {Function} returns.setRetrievalId - Set the assistant retrieval ID
 * @returns {Function} returns.clearItems - Clear conversation items
 */
export function useRealtimeConnection( { toggleMode } ) {
  const wavRecorderRef = useRef(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef(new WavStreamPlayer({ sampleRate: 24000 }));
  const clientRef = useRef(null);
  const retrievalIdRef = useRef(null);

  const [realtimeError, setRealtimeError] = useState(null);
  const [items, setItems] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [realtimeChatId, setRealtimeChatId] = useState(null);
  const [audioData, setAudioData] = useState(EMPTY_AUDIO);

  const setRetrievalId = useCallback((id) => {
    retrievalIdRef.current = id;
  }, []);

  const changeStatus = useCallback((newStatus = true) => {
    setIsConnected(newStatus);
    toggleMode(newStatus ? "realtime" : "transcribe");
  }, [toggleMode]);

  const disconnectConversation = useCallback(async () => {
    if (!isConnected) return;
    try {
      changeStatus(false);
      // setItems([]);

      const client = clientRef.current;
      client?.disconnect();

      const wavRecorder = wavRecorderRef.current;
      await wavRecorder.end();

      const wavStreamPlayer = wavStreamPlayerRef.current;
      wavStreamPlayer.interrupt();
    } catch (error) {
      console.log('[Realtime] error: ', error);
    }
  }, [isConnected, changeStatus]);

  const connectConversation = useCallback(async () => {
    if (!retrievalIdRef.current) {
      setRealtimeError('noRetrievalId');
      return;
    }
    if (!relayConfig.defaults.baseURL) {
      setRealtimeError('serverError');
      return;
    }

    try {
      // Establish connection with relay server
      clientRef.current = new RealtimeClient({ url: relayConfig.defaults.baseURL });

      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      // Handle WebSocket close events
      client.on('close', async (event) => {
        setRealtimeError('serverError');
      });

      client.on('conversation.updated', async ({ item, delta }) => {
        const items = client.conversation.getItems();
        if (delta?.audio) {
          wavStreamPlayer.add16BitPCM(delta.audio, item.id);
        }
        // Checks chat_id as a parameter on item
        if (item.chat_id && !realtimeChatId) {
          setRealtimeChatId(item.chat_id);
        }
        if (item.status === 'completed' && item.formatted.audio?.length) {
          const wavFile = await WavRecorder.decode(
            item.formatted.audio,
            24000,
            24000
          );
          item.formatted.file = wavFile;
        }
        setItems(items);
      });

      changeStatus(true);
      setItems(client.conversation.getItems());

      await wavStreamPlayer.connect();
      await client.connect();

      const tenantApiKeyMap = await getUserInfo();
      const { apiKey: authApiKey } = await getAuthHeadersData();
      const apiKey = getSelectedTenantApiKey(tenantApiKeyMap) || authApiKey || relayConfig.defaults.cognitiveApiKey || process.env.REACT_APP_API_KEY;

      // Manually send the ApiKey to the server
      client.realtime.send('connection.create', {
        apiKey: apiKey,
        assistantId: retrievalIdRef.current
        // api_key_openai: '...'
      });

      await wavRecorder.begin();

      if (client.getTurnDetectionType() === 'server_vad') {
        await wavRecorder.record((data) => client.appendInputAudio(data.mono));
      }
    } catch (error) {
      setRealtimeError(error);
    }
  }, [realtimeChatId, changeStatus]);

  const deleteConversationItem = useCallback(async (id) => {
    try {
      const client = clientRef.current;
      client?.deleteItem(id);
    } catch (error) {
      setRealtimeError(error);
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      if (!clientRef.current?.isConnected()) {
        setRealtimeError('clientNotConnected');
        return;
      }
      setIsRecording(true);
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;
      const trackSampleOffset = wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client?.cancelResponse(trackId, offset);
      }
      await wavRecorder.record((data) => client?.appendInputAudio(data.mono));
    } catch (error) {
      setRealtimeError(error);
    }
  }, []);

  const stopRecording = useCallback(async () => {
    try {
      setIsRecording(false);
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      await wavRecorder.pause();
      if (!client?.isConnected()) {
        setRealtimeError('clientNotConnected');
        return;
      }
      client?.createResponse();
    } catch (error) {
      setRealtimeError(error);
    }
  }, []);

  const cancelRecording = useCallback(async () => {
    try {
      if (!isRecording) return;
      setIsRecording(false);
      const wavRecorder = wavRecorderRef.current;
      await wavRecorder.pause();
    } catch (error) {
      setRealtimeError(error);
    }
  }, [isRecording]);

  const clearItems = useCallback(() => {
    setItems([]);
  }, []);

  // Audio visualization: update audioData from frequency data
  useEffect(() => {
    let animationFrameId;
    let isLoaded = true;

    const render = () => {
      if (isLoaded && isRecording && wavRecorderRef.current) {
        const result = wavRecorderRef.current.getFrequencies("voice");
        setAudioData(result.values);
        animationFrameId = window.requestAnimationFrame(render);
      }
    };

    if (isRecording) {
      render();
    } else {
      setAudioData(EMPTY_AUDIO);
    }

    return () => {
      isLoaded = false;
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isRecording]);

  return {
    items,
    isConnected,
    isRecording,
    audioData,
    wavRecorderRef,
    wavStreamPlayerRef,
    clientRef,
    connectConversation,
    disconnectConversation,
    deleteConversationItem,
    startRecording,
    stopRecording,
    cancelRecording,
    realtimeError,
    realtimeChatId,
    setRetrievalId,
    clearItems,
  };
}
